import React, { useState, useEffect, useRef } from 'react';
import { GripVertical, Download, History, SlidersHorizontal, Trash2, RefreshCw, Flame } from 'lucide-react';

/**
 * The standard 6-dot actions menu.
 *
 * Two ways to fill it. The named props below cover the standard CRUD verbs every
 * grid shares. `items` is for pages whose actions are their own — a workflow page
 * has Submit/Approve/Publish, which no fixed prop list can express. Before `items`
 * existed those pages hand-rolled the same markup with the stui-action-menu-*
 * classes; prefer passing items here instead of growing a third copy.
 *
 * items: [{ icon, label, onClick, disabled, danger, hidden }]
 * They render above the named actions, in the order given. An entry of the form
 * { divider: 'subtle' | 'strong' } draws a divider; leading, trailing and repeated
 * dividers are dropped so conditional items never leave a stray rule.
 *
 * Named actions render in the standard order: Refresh, Modify View, export
 * (downloadLabel), Audit Trail, then a strong divider and the destructive group
 * (Bulk Delete, Purge Old Records).
 */
const ActionMenu = ({ items, disabled, onRefresh, onDownload, downloadLabel = 'Export Excel', onModifyView, showModifyView = false, onBulkDelete, showBulkDelete = false, onChangeHistory, showChangeHistory = false, onPurge, showPurge = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleRefresh = () => {
    setIsOpen(false);
    onRefresh?.();
  };

  const handleDownload = () => {
    setIsOpen(false);
    onDownload?.();
  };

  const handleModifyView = () => {
    setIsOpen(false);
    onModifyView?.();
  };

  const handleBulkDelete = () => {
    setIsOpen(false);
    onBulkDelete?.();
  };

  // Flat list of entries, then dividers trimmed so none leads, trails or repeats.
  const raw = [
    ...(items || []).filter(it => it && !it.hidden).map((it, i) => it.divider
      ? { divider: it.divider, key: it.key ?? `div-${i}` }
      : { ...it, key: it.key ?? i, title: it.title ?? (typeof it.label === 'string' && it.label.length > 32 ? it.label : undefined), onClick: () => { setIsOpen(false); it.onClick?.(); } }),
    ...(onRefresh ? [{ key: 'refresh', icon: <RefreshCw size={16} />, label: 'Refresh', onClick: handleRefresh }] : []),
    ...(showModifyView ? [{ key: 'modify-view', icon: <SlidersHorizontal size={16} />, label: 'Modify View', onClick: handleModifyView }] : []),
    ...(onDownload ? [{ key: 'download', icon: <Download size={16} />, label: downloadLabel, onClick: handleDownload }] : []),
    ...(showChangeHistory ? [{ key: 'audit-trail', icon: <History size={16} />, label: 'Audit Trail', onClick: () => { setIsOpen(false); onChangeHistory?.(); } }] : []),
    ...((showBulkDelete || showPurge) ? [{ divider: 'strong', key: 'danger-divider' }] : []),
    ...(showBulkDelete ? [{ key: 'bulk-delete', icon: <Trash2 size={16} />, label: 'Bulk Delete', danger: true, onClick: handleBulkDelete }] : []),
    ...(showPurge ? [{ key: 'purge', icon: <Flame size={16} />, label: 'Purge Old Records…', danger: true, onClick: () => { setIsOpen(false); onPurge?.(); } }] : []),
  ];
  const entries = [];
  raw.forEach(e => {
    if (e.divider) {
      const prev = entries[entries.length - 1];
      if (!prev) return;
      if (prev.divider) { if (e.divider === 'strong') prev.divider = 'strong'; return; }
    }
    entries.push({ ...e });
  });
  while (entries.length && entries[entries.length - 1].divider) entries.pop();

  return (
    <div className="stui-action-menu" ref={menuRef}>
      <button
        className={`stui-action-menu-btn ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
        disabled={disabled}
        title={disabled ? 'No actions available' : 'Actions'}
      >
        <GripVertical size={16} />
      </button>
      {isOpen && !disabled && (
        <div className="stui-action-menu-dropdown">
          {entries.map((e, i) => e.divider ? (
            <div key={e.key ?? `d${i}`} className={e.divider === 'strong' ? 'stui-action-menu-divider-strong' : 'stui-action-menu-divider'} />
          ) : (
            <button
              key={e.key ?? i}
              className={`stui-action-menu-item${e.danger ? ' stui-action-menu-item-danger' : ''}`}
              onClick={e.onClick}
              disabled={e.disabled}
              title={e.title}
            >
              {e.icon}
              <span>{e.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
